import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { PlanStarter } from "@heraion/shared";
import {
  InboxProvider,
  type BellTarget,
  LeaveGuardProvider,
  NotificationBell,
  resolveTheme,
  useBreakpoint,
  useLeaveGuard,
} from "@heraion/ui";
import { apiFetch } from "./api";
import { useThemePalette } from "./theme";
import { useAuth, type AuthUser } from "./auth/AuthContext";
import { SettingsScreen, type SettingsSubKey } from "./SettingsScreen";
import { AccountMenu } from "./AccountMenu";
import { useExperience } from "./experience";
import { useCoachStatus } from "./useCoachStatus";
import { Sidebar } from "./shell/Sidebar";
import { TabBar, TAB_BAR_HEIGHT, CREATE_NAV_OVERHANG } from "./shell/TabBar";
import { TopBar } from "./shell/TopBar";
import { CONTENT_MAX, makeStyles } from "./shell/appShellStyles";
// The Me sections (ME-1 / MS-1) are the athlete's own data, reached from the
// account menu; their content pane is extracted to keep this shell small.
import { MeContentPane } from "./shell/MeContentPane";
import type { MeKey } from "./shell/meNav";
// The per-experience tab list, split out to keep this shell small.
import { useAppShellSurfaces } from "./shell/useAppShellSurfaces";
import { useCreateHubWeb, type ComposerRequest } from "./useCreateHubWeb";
import { useInboxFormat } from "./shell/useInboxFormat";

// Signed-in chrome for web-rnw. A TopBar (brand · account menu, the account
// pinned right) sits above BOTH layouts of the SAME nav state, chosen by
// viewport width (useBreakpoint), never by platform:
//   • wide (desktop console) → a left nav rail (Sidebar) + content beside it.
//   • narrow (phone / narrow browser) → content, then a BOTTOM tab bar
//     overlaying it (RW-52 — mirrors mobile's bottom tabs; it used to sit at
//     the top, under the TopBar).
// The account menu (role switch + Me + Settings + sign-out) is the identity
// slot in both; the rail/tab bar carries the surface nav PLUS the raised
// Create affordance, in mobile's own position (RW-52 — it used to be a
// TopBar button). State-based navigation (no router); URL routing can layer
// on later. Each screen stays a plain RN tree so it also mounts under
// mobile's react-navigation; this shell is the only web-rnw-specific piece.

export function AppShell({ user }: { user: AuthUser }) {
  return (
    <LeaveGuardProvider>
      <AppShellInner user={user} />
    </LeaveGuardProvider>
  );
}

function AppShellInner({ user }: { user: AuthUser }) {
  const { guardedLeave } = useLeaveGuard();
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const { dark, p, family } = useThemePalette();
  const styles = makeStyles(p);
  // RW-52 — the narrow bottom TabBar overlays the body rather than sitting in
  // flow (mirrors mobile's fixed bottom tabs), so the body's own scroll
  // container needs the bar's height PLUS the device's bottom safe-area
  // inset (home indicator / gesture bar) added to its padding — a measured
  // pad, not a magic margin.
  const { bottom: bottomInset } = useSafeAreaInsets();

  // Experience = role-gated surface set (athlete / coach / admin). Settings
  // lives in ALL; the feedback inbox is the admin experience, the team overview
  // is the coach experience (CO-8) — neither is a stray athlete tab. Coach shows
  // once the caller has an active coach edge.
  // One edge read serves both: the gate (isCoach) and the dashboard's roster.
  // `ready` keeps the experience guard from clobbering a persisted 'coach'
  // choice before the edges land (CO-15).
  const { isCoach, athleteSubs, ready: coachReady } = useCoachStatus(user.sub);
  const { available, active: experience, setActive: setExperience } =
    useExperience(user.admin, isCoach, coachReady);

  // Active surface key WITHIN the experience. Declared before `surfaces` so
  // the Plan tab's onOpenProgress seam can flip to "progress".
  const [activeTab, setActiveTab] = useState("plan");

  // Me (ME-1) is a second nav axis, orthogonal to the rail tabs: the avatar-menu
  // picks a Me section, and while one is open it takes over the content pane.
  // Null = showing the active tab. Selecting any tab clears it (selectTab).
  const [meSub, setMeSub] = useState<MeKey | null>(null);
  // The ONE way to change tab / meSub. goTab is the raw, unguarded change and
  // is only ever called from inside guardedLeave; every other path (rail, hub,
  // start-workout, account menu, Me back, experience switch) goes through the
  // guarded functions below, so a dirty Targets editor always confirms first.
  const goTab = useCallback((key: string) => {
    setMeSub(null);
    setActiveTab(key);
  }, []);
  const selectTab = useCallback(
    (key: string) => guardedLeave(() => goTab(key)),
    [guardedLeave, goTab],
  );
  const selectMe = useCallback(
    (k: MeKey | null) => guardedLeave(() => setMeSub(k)),
    [guardedLeave],
  );

  // Cross-tab deep-link into a Settings sub-screen (e.g. the pain map from
  // the Home card). Consume-then-clear, same shape as
  // composerRequest below; the shared renderSettings() closure wires it into
  // every "settings" surface entry. Both memoized so the surfaces useMemo
  // below only recomputes when settingsSub actually changes, not every
  // render (selectTab/setSettingsSub are stable setters). The pain map is
  // reached from the Home card and the veto line.
  const [settingsSub, setSettingsSub] = useState<SettingsSubKey | null>(null);
  const renderSettings = useCallback(
    () => (
      <SettingsScreen
        requestedSub={settingsSub}
        onRequestedSubHandled={() => setSettingsSub(null)}
      />
    ),
    [settingsSub],
  );
  const requestOpenSettings = useCallback(
    (sub: SettingsSubKey) =>
      guardedLeave(() => {
        setSettingsSub(sub);
        goTab("settings");
      }),
    [guardedLeave, goTab],
  );

  // A server bell row that leads somewhere (#296): the missing bodyweight opens Me → Measurements. The
  // health-connect target is the phone's; the web has none.
  const openBellTarget = useCallback(
    (target: BellTarget) => {
      if (target.to !== "settingsMeasurements") return false;
      selectMe("corpo");
      return true;
    },
    [selectMe],
  );

  // Create-hub (HR-S4w): the sidebar "Crea" CTA opens the same shared hub
  // mobile's tab-bar `+` does. Hub intents land here as a composer request
  // the Plan surface (program/plan) or Workouts surface (RW-18: workout)
  // consumes on mount (state-based nav — no router).
  const [composerRequest, setComposerRequest] =
    useState<ComposerRequest | null>(null);
  // CO-19 — a coach's program/plan request has no "plan" surface to land on
  // (coach mounts coachDashboard/library/settings, not Plan); it goes to the
  // new "library" surface (CoachLibraryScreen) instead. "workout" still
  // targets "workouts" regardless of experience — coach has no logger
  // surface either, so that request stays a no-op for coach, same known gap
  // as mobile's useCreateHubNav (see its own comment).
  // #359: Workouts' «Plan a program» after «Save as model» routes here too.
  const requestComposer = useCallback(
    (req: ComposerRequest) => {
      guardedLeave(() => {
        goTab(
          experience === "coach" && (req.kind === "program" || req.kind === "plan")
            ? "library"
            : req.kind === "workout"
              ? "workouts"
              : "plan",
        );
        setComposerRequest(req);
      });
    },
    [guardedLeave, goTab, experience],
  );
  const { openHub, closeHub, hubNode } = useCreateHubWeb(
    user.sub,
    experience,
    requestComposer,
    // CO-21 — same single useCoachStatus read above feeds the hub's own
    // library section's AssignSheet roster; `undefined` while unresolved,
    // same "not yet known" guard as the Plan/Library surfaces' own
    // athleteRoster prop (PE-48 review fix #3).
    coachReady ? athleteSubs : undefined,
  );
  // Plan tab's "Start" (today's session / a program-highlight card) / "New
  // workout" — the SAME cross-tab dispatch the Create-hub's "New workout"
  // uses above, optionally carrying a pre-built PlanStarter. Passed down to
  // PlanScreen; useWorkoutStarter.ts builds the starter, this only routes
  // it to the Workouts tab.
  const requestStartWorkout = useCallback(
    (starter?: PlanStarter) =>
      guardedLeave(() => {
        goTab("workouts");
        setComposerRequest({ kind: "workout", ...(starter ? { planStarter: starter } : {}) });
      }),
    [guardedLeave, goTab],
  );
  // RW-52 — whether + (narrow bar only, RW-52.1) where the Create affordance
  // mounts: after Workouts for athlete (Home, Workouts, +, Progress, Me, the centre), after the
  // coach dashboard for coach (CO-19 — a coach authors programs/plans for
  // their roster too, same criterion as TabShell's showFab on mobile).
  // Undefined → admin, which has none either — the rail (Sidebar) now only
  // reads this as a yes/no gate (RW-52.1: its button has no list position to
  // anchor to any more), the narrow bar (TabBar) still reads it as a position.
  // Mobile twin: the position of the `Create` route in apps/mobile experienceTabs.tsx.
  const createAfterKey =
    experience === "athlete"
      ? "workouts"
      : experience === "coach"
        ? "coachDashboard"
        : undefined;
  // Same accessibility label mobile's CreateFab uses for the identical
  // affordance, so the two read as one control to a screen-reader user
  // moving between platforms. Narrow bar only — its circle stays icon-only
  // (RW-52.1 doesn't touch it), so this is its accessible name, not visible
  // text.
  const createAccessibleLabel = t("createHub.open", { defaultValue: "Create" });
  // RW-52.1 — the rail's button now shows the word too, not just an icon.
  // `createHub.title` is the SAME word already used as the create-hub
  // modal's own visible header (useCreateHubWeb.tsx), reused here rather
  // than minting a new key for an identical string.
  const createVisibleLabel = t("createHub.title", { defaultValue: "Create" });

  // Each experience's nav tabs. Settings is shared across both. The athlete
  // set is the Plan hub + Progress; admin is the feedback inbox. Split out
  // to useAppShellSurfaces (file-size cap, prompt.md §Limiti di dimensione):
  // same three lists, same deps, same memo, no behaviour change.
  const surfaces = useAppShellSurfaces({
    experience,
    t,
    dark,
    composerRequest,
    setComposerRequest,
    athleteSubs,
    coachReady,
    callerSub: user.sub,
    renderSettings,
    requestOpenSettings,
    requestStartWorkout,
    requestComposer,
    selectTab,
    openCreateHub: openHub,
  });

  // When the experience changes, point at its first tab (athlete→plan,
  // admin→inbox) so a stale key (e.g. "plan" while in admin) never leaves
  // the nav with nothing highlighted. Runs AFTER the guarded experience pick
  // (onSelectExperience), so it is not a second unguarded exit. The hub is athlete-only chrome:
  // close it too so it can't linger open across the switch.
  useEffect(() => {
    setActiveTab(
      experience === "admin"
        ? "inbox"
        : experience === "coach"
          ? "coachDashboard"
          : "plan",
    );
    setMeSub(null);
    closeHub();
  }, [experience, closeHub]);

  const current = surfaces.find((s) => s.key === activeTab) ?? surfaces[0]!;
  const { wide } = useBreakpoint();

  // The surface tabs the rail / nav bar shows. Settings is NOT among them — it
  // lives in the account menu now — but it stays a real surface (in `surfaces`)
  // so the account menu can open it via selectTab("settings").
  const navItems = surfaces.filter((s) => s.key !== "settings" && !s.hidden);

  // The account menu (identity slot), anchored top-right of the TopBar in both
  // layouts. It routes to the three axes the shell owns: a Me section (meSub),
  // a surface (Settings), or an experience switch. Wide shows the email; narrow
  // is avatar-only (compact).
  const accountMenu = (compact: boolean): ReactNode => (
    <AccountMenu
      email={user.email}
      activeKey={meSub}
      activeSettings={!meSub && current.key === "settings"}
      experiences={available}
      activeExperience={experience}
      onSelectMe={selectMe}
      onSelectExperience={(x) => guardedLeave(() => setExperience(x))}
      onOpenSettings={() => selectTab("settings")}
      onSignOut={signOut}
      placement="bottom"
      align="right"
      compact={compact}
    />
  );

  // ONE tree shape for both layouts (MX-8), not two `if (wide) return`
  // branches. React matches children by (type, position); the old branches
  // put the Sidebar and the content pane at DIFFERENT positions between wide
  // and narrow, so crossing the breakpoint tore down and rebuilt every
  // surface underneath (MX-8 measured `editingSlug` reset on the Maxes
  // editor, but any surface under `current.render()` / `MeContentPane` was
  // exposed the same way). Now the row's children are ALWAYS
  // [Sidebar-or-null, content pane] and the shell's are ALWAYS [TopBar,
  // row, TabBar-or-null, hub]: `wide` only changes style, props
  // and visibility at a fixed slot, so the content pane stays the same
  // instance across a resize.
  //
  // Desktop console (wide): a top bar (brand · account) over a row of the
  // nav rail + content; Create lives in the rail (RW-52), so the TopBar
  // carries only identity. Narrow: the same TopBar, then content, then a
  // BOTTOM tab bar overlaying it (RW-52, mirrors mobile's bottom tabs).
  // `showNav` gates both the tab bar and the body's extra bottom padding for
  // it (admin's single surface has neither); wide's Sidebar always shows,
  // even for one item, so it needs neither either.
  //
  // ONE useInbox call for the whole shell (single-fetch fix,
  // CN-coach-edges-fetch's rule): the TopBar bell, the logger's bell and
  // Home's InboxSection all read this SAME instance via useInboxContext().
  // INBOX-10: every role in one read, each card names its own role.
  const showNav = navItems.length > 1;
  const inboxHost = useInboxFormat();
  const contentStyle = wide
    ? styles.contentWide
    : [
        styles.bodyInner,
        showNav && {
          paddingBottom: TAB_BAR_HEIGHT + CREATE_NAV_OVERHANG + bottomInset,
        },
      ];

  return (
    <InboxProvider apiFetch={apiFetch} openTarget={openBellTarget} {...inboxHost}>
      <View style={styles.shell}>
        <TopBar
          p={p}
          brand={t("brand.product")}
          right={
            <>
              <NotificationBell theme={resolveTheme(dark, family)} bottomInset={bottomInset} />
              {accountMenu(!wide)}
            </>
          }
          maxWidth={wide ? undefined : CONTENT_MAX}
        />

        <View style={wide ? styles.shellRow : styles.body}>
          {wide ? (
            <Sidebar
              p={p}
              items={navItems.map((s) => ({ key: s.key, label: s.label }))}
              // Settings isn't a rail row, so while it's the active surface no rail
              // row matches activeKey: correct, nothing highlights for it.
              activeKey={meSub ? "" : current.key}
              onSelect={selectTab}
              onCreatePress={createAfterKey ? openHub : undefined}
              createLabel={createAfterKey ? createVisibleLabel : undefined}
            />
          ) : null}
          <View style={contentStyle}>
            {meSub ? (
              <MeContentPane meSub={meSub} wide={wide} p={p} onBack={() => selectMe(null)} />
            ) : (
              <>
                {/* Single-surface users have no nav bar to name the surface, so
                    the shell names it once here, narrow only: wide's Sidebar
                    already lists the one item, naming the surface on its own. */}
                {!wide && navItems.length === 1 && (
                  <Text style={styles.soloTitle}>{current.label}</Text>
                )}
                {current.render()}
              </>
            )}
          </View>
        </View>

        {!wide && showNav ? (
          <TabBar
            p={p}
            items={navItems}
            activeKey={meSub ? "" : activeTab}
            onSelect={selectTab}
            createAfterKey={createAfterKey}
            onCreatePress={openHub}
            createLabel={createAccessibleLabel}
            bottomInset={bottomInset}
          />
        ) : null}
        {experience === "athlete" || experience === "coach" ? hubNode : null}
      </View>
    </InboxProvider>
  );
}
